/**
 * Used only to pass data to the machine initialization.
 */
import { useMemo } from 'react'

import { useExtensionData } from '$extensionSrc/hooks/extensionStorageListener'
import {
  isValidRecommendationKey,
  RecommendationKeyEnum,
} from '$extensionSrc/recommendations/recommendationTypes'
import { PLATFORM, STORAGE_KEY } from '$extensionSrc/utils/enums'
import * as localStorage from '$extensionSrc/utils/localStorage'

export type BatchUpdateAutomationErrors = Partial<
  Record<RecommendationKeyEnum, { name: string; message: string }>
>
export type BatchUpdateAutomationErrorsBlob = Partial<
  Record<PLATFORM, BatchUpdateAutomationErrors>
>

export function parseBatchUpdateAutomationErrorsBlob(
  blob: any,
): BatchUpdateAutomationErrorsBlob {
  if (!blob || typeof blob !== 'object') {
    return {}
  }

  const result: BatchUpdateAutomationErrorsBlob = {}

  for (const [key, value] of Object.entries(blob)) {
    if (
      // Validate that key is a valid PLATFORM; if not, skip key
      key in PLATFORM &&
      // Validate that value is an object; if not, skip key
      typeof value === 'object' &&
      value !== null &&
      // Validate that all items in object are valid RecommendationKeyEnum values
      Object.keys(value).every(
        (item) => typeof item === 'string' && isValidRecommendationKey(item),
      )
    ) {
      result[key as PLATFORM] = value as BatchUpdateAutomationErrors
    }
  }

  return result
}

export async function getBlob(): Promise<BatchUpdateAutomationErrorsBlob> {
  const blob = await localStorage.getSingle(
    STORAGE_KEY.BATCH_UPDATE_AUTOMATION_ERRORS,
  )
  return parseBatchUpdateAutomationErrorsBlob(blob)
}

export async function getBatchUpdateAutomationErrors(
  platform: PLATFORM,
): Promise<BatchUpdateAutomationErrors> {
  const blob = await getBlob()

  const batchUpdateAutomationErrors = blob?.[platform]

  if (batchUpdateAutomationErrors) {
    return batchUpdateAutomationErrors
  }

  return {}
}

export async function setBatchUpdateAutomationErrors(
  platform: PLATFORM,
  errors: BatchUpdateAutomationErrors,
): Promise<void> {
  const blob = await getBlob()

  return localStorage.set({
    [STORAGE_KEY.BATCH_UPDATE_AUTOMATION_ERRORS]: {
      ...blob,
      [platform]: errors,
    },
  })
}

export async function clearBatchUpdateAutomationErrors(
  platform: PLATFORM,
): Promise<void> {
  const blob = await getBlob()

  delete blob[platform]

  return localStorage.set({
    [STORAGE_KEY.BATCH_UPDATE_AUTOMATION_ERRORS]: blob,
  })
}

export function useBatchUpdateAutomationErrors(
  platform: PLATFORM,
): BatchUpdateAutomationErrors | undefined {
  const { data, isLoaded } = useExtensionData({
    local: [STORAGE_KEY.BATCH_UPDATE_AUTOMATION_ERRORS],
  })

  return useMemo(() => {
    if (!isLoaded || !data) {
      return undefined
    }

    const blob = parseBatchUpdateAutomationErrorsBlob(
      data[STORAGE_KEY.BATCH_UPDATE_AUTOMATION_ERRORS],
    )
    return blob?.[platform] || {}
  }, [platform, isLoaded, data])
}
